import { buildWhatsappLink, normalizeContactPhone } from "./customer-contact";
import {
  client_objectives,
  nawy_offices,
  permissionLevelMap,
} from "@/modules/common/constants";
import {
  EnvironmentVariables,
  OptionProps,
  Permission,
  PermissionLevel,
  ToastParams,
  ToastType,
} from "@/modules/common/types";
import { muiTheme } from "@/styles/theme";
import { getContrastRatio } from "@mui/material/styles";
import { UUID } from "crypto";
import {
  differenceInHours,
  differenceInMinutes,
  isToday,
  isTomorrow,
  isYesterday,
} from "date-fns";
import { AddReferralRequest } from "@/modules/lead-page/dtos/index-v2";
import { SalesResponseV2 } from "@/modules/sales/dtos";
import { Row } from "react-table";
import { ToastOptions, UpdateOptions, toast } from "react-toastify";
import { Option } from "@/modules/common/components/input/dropdowns/auto-complete";

export const getUserNameAndInitials = (name: string) => {
  const getUserName = (name: string): string => {
    return name.split(" ").slice(0, 2).join(" ");
  };

  const getInitials = (name: string): string => {
    const username = getUserName(name);
    const initials = username
      .split(" ")
      .map((word) => word.charAt(0))
      .join("");
    return initials.toUpperCase();
  };

  const userName = getUserName(name);
  const userInitials = getInitials(name);

  return {
    userName,
    userInitials,
  };
};

export const convertCamelCaseToWords = (str: string) => {
  return str
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

export const filterRows = (
  rows: Row<object>[],
  searchValue: string | undefined
) => {
  const filteredRows = rows.filter((row) => {
    const search = row.cells.some((cell) => {
      const cellValue = cell.value?.name || cell.value;
      return String(cellValue)
        .toLowerCase()
        .includes((searchValue || "").toLowerCase());
    });
    return search;
  });
  return filteredRows;
};

export function filterUniqueStatuses(
  statuses: {
    name: string;
    color: string;
    id: UUID;
    isDeleted: boolean;
  }[]
) {
  if (!statuses) {
    return [];
  }

  const seenStatuses = new Set<string>();
  return statuses.filter((item) => {
    if (item.isDeleted || seenStatuses.has(item.name)) {
      return false;
    } else {
      seenStatuses.add(item.name);
      return true;
    }
  });
}

export function isAppleDevice() {
  const userAgent = navigator.userAgent || navigator.vendor;

  const isIphone = /iPhone/.test(userAgent);

  const isSafari =
    /Safari/.test(userAgent) &&
    !/Chrome/.test(userAgent) &&
    !/CriOS/.test(userAgent);

  return { isIphone, isSafari };
}

export const getContrastColor = (
  backgroundColor: string | null | undefined
): string => {
  const defaultColor = muiTheme.colorSchemes.light.palette.main.black;

  if (!backgroundColor) {
    return defaultColor;
  }

  try {
    const isValidColor =
      /^(#[0-9A-F]{3,6}|rgba?\([^)]{1,50}\)|hsla?\([^)]{1,50}\))$/i.test(
        backgroundColor
      );

    if (!isValidColor) {
      return defaultColor;
    }

    const contrastRatio = getContrastRatio(backgroundColor, "#ffffff");
    return contrastRatio >= 3.5 ? "#ffffff" : defaultColor;
  } catch (error) {
    console.warn(`Invalid color value: ${backgroundColor}`);
    return defaultColor;
  }
};

export const createOptionsWithColors = (
  list: any,
  field1: string,
  field2: string
) => {
  const options = [];
  let option;
  let color;

  for (let i = 0; i < list?.length; i++) {
    color =
      !list[i]?.color || list[i]?.color === "rgba(255,255,255,0.7)"
        ? "black"
        : list[i]?.color;
    option = {
      id: list[i][field1],
      label: list[i][field2],
      color: color,
    };
    options.push(option);
  }

  return options;
};

export const formatTextBetweenDates = (
  firstDate: string,
  secondDate: string
): string => {
  const date1 = new Date(firstDate);
  const date2 = new Date(secondDate);

  const diffInMinutes = differenceInMinutes(date2, date1);
  const diffInHours = differenceInHours(date2, date1);
  const secondsInDay = 24 * 60 * 60 * 1000;
  const milliseconds = date2.getTime() - date1.getTime();
  const roundDays = (days: number): number => {
    const fractionalPart = days - Math.floor(days);

    if (fractionalPart > 0.5) {
      return Math.round(days);
    } else {
      return Math.round(days);
    }
  };
  const diffInDays = roundDays(milliseconds / secondsInDay);

  if (isToday(date1) && isToday(date2)) {
    if (diffInMinutes === 0) {
      return "Now";
    }

    if (Math.abs(diffInMinutes) < 60) {
      return `${Math.abs(diffInMinutes)} minute${
        Math.abs(diffInMinutes) > 1 ? "s" : ""
      } ${diffInMinutes < 0 ? "ago" : "left"}`;
    }

    if (Math.abs(diffInHours) < 24) {
      return `${Math.abs(diffInHours)} hour${
        Math.abs(diffInHours) > 1 ? "s" : ""
      } ${diffInHours < 0 ? "ago" : "left"}`;
    }
  }

  if (isYesterday(date2) && isToday(date1)) {
    return "Yesterday";
  }

  if (isTomorrow(date2) && isToday(date1)) {
    return "Tomorrow";
  }

  if (diffInDays > 0) {
    return `Less than ${Math.abs(diffInDays)} day${diffInDays > 1 ? "s" : ""}`;
  }

  if (diffInDays < 0) {
    return `${Math.abs(diffInDays)} day${
      Math.abs(diffInDays) > 1 ? "s" : ""
    } ago`;
  }

  return `${Math.abs(diffInDays)} day${
    Math.abs(diffInDays) > 1 ? "s" : ""
  } ago`;
};

export const showToast = ({
  type,
  message,
  toastId,
  options = {},
}: ToastParams): void => {
  const defaultOptions: ToastOptions = {
    position: "bottom-left",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  };

  const mergedOptions: ToastOptions = {
    ...defaultOptions,
    ...options,
    toastId,
  };

  if (toast.isActive(toastId)) {
    const updateOptions: UpdateOptions = {
      render: message,
      type: type,
      ...mergedOptions,
    };
    toast.update(toastId, updateOptions);
  } else {
    switch (type) {
      case ToastType.SUCCESS:
        toast.success(message, mergedOptions);
        break;
      case ToastType.ERROR:
        toast.error(message, mergedOptions);
        break;
      case ToastType.INFO:
        toast.info(message, mergedOptions);
        break;
      case ToastType.WARNING:
        toast.warning(message, mergedOptions);
        break;
      default:
        toast(message, mergedOptions);
    }
  }
};
export const createOptions = ({
  list,
  field1,
  field2,
}: {
  list: any;
  field1: string;
  field2: string;
}) => {
  const options = [];
  let option;

  for (let i = 0; i < list?.length; i++) {
    option = { value: list[i][field1], label: list[i][field2] };
    options.push(option);
  }

  return options as OptionProps[];
};

export const createFieldOptions = (
  fieldType: string,
  {
    compoundOptions,
    developerOptions,
    propertyTypes,
  }: {
    compoundOptions?: OptionProps[];
    developerOptions?: OptionProps[];
    propertyTypes?: OptionProps[];
  }
): OptionProps[] | undefined => {
  switch (fieldType) {
    case "client_objective":
      return client_objectives;
    case "nawy_office":
      return nawy_offices;
    case "all_compounds":
      return compoundOptions || [];
    case "developer_office":
      return developerOptions || [];
    case "property_type":
      return propertyTypes || [];
    default:
      return [];
  }
};

export const containsArabic = (text: string) => {
  const arabicPattern = /[\u0600-\u06FF]/;
  return typeof text === "string" && arabicPattern.test(text);
};

export const isLevelAllowed = ({
  currentLevel,
  requiredLevel,
}: {
  currentLevel: PermissionLevel;
  requiredLevel: PermissionLevel;
}) => {
  const currentLevelValue = permissionLevelMap.get(currentLevel);
  const requiredLevelValue = permissionLevelMap.get(requiredLevel);

  if (!currentLevelValue || !requiredLevelValue) return false;

  return currentLevelValue >= requiredLevelValue;
};

export const userHasPermission = (
  permission: Permission,
  currentUserPermissions?: Permission[]
) => {
  // const userRoles = localStorage.getItem("userRoles");

  // if (userRoles?.includes("crm-super-admin")) {
  //   return true;
  // }

  return currentUserPermissions?.some(
    (userPermission) =>
      userPermission.name === permission.name &&
      isLevelAllowed({
        currentLevel: userPermission.level,
        requiredLevel: permission.level,
      })
  );
};

export const isOlderThanXDays = (
  timestamp: string | Date,
  days: number
): boolean => {
  const currentDate = new Date();
  const pastDate = new Date(currentDate);
  pastDate.setDate(currentDate.getDate() - days);

  const timestampDate =
    typeof timestamp === "string" ? new Date(timestamp) : timestamp;

  return timestampDate >= pastDate && timestampDate <= currentDate;
};

export const formatCurrency = (value: string): string => {
  if (!value) return "";
  const cleanValue = value.replace(/[^0-9.]/g, "");
  const number = parseFloat(cleanValue);
  if (isNaN(number)) return "";
  return number.toLocaleString("en-US");
};

export const parseNumber = (value: string): number => {
  if (!value) return 0;
  const cleanValue = value.replace(/,/g, "");
  const number = parseFloat(cleanValue);
  return isNaN(number) ? 0 : number;
};

export function omit<T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> {
  const result = { ...obj };
  keys.forEach((key) => delete result[key]);
  return result;
}

export const getErrorMessage = (error: {
  response: { data: { message: string; statusCode: number } };
}) => {
  const statusMessages: Record<string, string> = {
    "401": `Unauthorized, ${error.response.data.message}`,
    "403": `Forbidden, ${error.response.data.message}`,
    "404": `Not Found, ${error.response.data.message}`,
    "409": `Conflict, ${error.response.data.message}`,
    "429": `Too Many Requests, ${error.response.data.message}`,
    "500": `Internal Server Error, ${error.response.data.message}`,
    "502": `Bad Gateway, ${error.response.data.message}`,
    "503": `Service Unavailable, ${error.response.data.message}`,
    "504": `Gateway Timeout, ${error.response.data.message}`,
  };

  const foundStatus = Object.entries(statusMessages).find(([status]) =>
    error.response.data.statusCode.toString().includes(status)
  );

  return foundStatus?.[1] || error.response.data.message;
};

export const BULK_MOVE_LOCK_MESSAGE =
  "This lead is being updated right now. Try again in a few minutes.";

export const BULK_MOVE_IN_PROGRESS_MESSAGE =
  "Only one bulk move at a time. Wait for the current one to finish.";

// The backend returns 409 with a "bulk" message when a journey is held by an
// in-flight bulk move (or when a second bulk move is attempted).
export const isBulkMoveConflictError = (error: unknown): boolean => {
  const err = error as {
    response?: { status?: number; data?: { message?: string } };
  };
  return (
    err?.response?.status === 409 &&
    (err?.response?.data?.message?.toLowerCase().includes("bulk") ?? false)
  );
};

export const mapReferralValues = (values?: {
  [key: string]: any;
}): Partial<AddReferralRequest> | null => {
  if (!values) return null;
  return {
    ...(values?.propertyTypeId && {
      propertyTypeId: values.propertyTypeId.value,
    }),
    ...(values?.referralBudget && {
      referralBudget: values.referralBudget,
    }),
    ...(values?.referralNumberOfUnits && {
      referralNumberOfUnits: values.referralNumberOfUnits,
    }),
    ...(values?.comment && {
      comment: values.comment,
    }),
  };
};

export function capitalizeName(name?: string | null): string {
  if (!name) return "";
  const nameParts = name.split(" ");
  const capitalizedParts = nameParts.map(
    (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
  );
  return capitalizedParts.join(" ");
}

export const convertNumbersToStrings = (numbers: number[]): string[] => {
  return numbers.map((number) => number.toString());
};

export const selectChangeHandler = (
  name: string,
  value: Option | Option[] | null,
  prevFilters: any
) => {
  const next = { ...prevFilters };
  if (!value || (Array.isArray(value) && value.length === 0)) {
    delete next[name];
    return next;
  }

  if (Array.isArray(value)) {
    const ids = value.map((v) => v.value).filter(Boolean);
    if (ids.length === 0) {
      delete next[name];
    } else {
      next[name] = ids;
    }
  } else {
    next[name] = value.value;
  }

  return next;
};

export const trimSpacesIfDigitsOrPrefixed = (str: string) => {
  const stringWithoutSpaces = str.replace(/\s+/g, "");

  if (
    /^\d+$/.test(stringWithoutSpaces) ||
    /^\+\d+$/.test(stringWithoutSpaces)
  ) {
    return stringWithoutSpaces;
  }
  return str;
};

export const textChangeHandler = (
  name: string,
  value: string,
  prevFilters: any
) => {
  const newFilters = { ...prevFilters };

  const processedValue = trimSpacesIfDigitsOrPrefixed(value);

  if (processedValue.length === 0) {
    delete newFilters[name];
  } else {
    newFilters[name] = processedValue;
  }

  return newFilters;
};

export const booleanChangeHandler = (
  name: string,
  value: string,
  prevFilters: any
) => {
  const newFilters = { ...prevFilters };

  if (!value) {
    delete newFilters[name];
  } else {
    newFilters[name] = "";
  }

  return newFilters;
};

export const handleSelectSummary = ({
  key,
  label,
  value,
  prevSummary,
}: {
  key: string;
  value: Option | Option[] | null;
  label: string;
  prevSummary: any;
}) => {
  const newSummary = { ...prevSummary };
  if (value && Array.isArray(value) && value.length) {
    newSummary[key] = {
      label,
      value: value.map((item) => item.label),
    };
  } else if (value && !Array.isArray(value)) {
    newSummary[key] = {
      label,
      value: value.label,
    };
  } else {
    delete newSummary[key];
  }

  return newSummary;
};

export const handleTextSummary = ({
  prevFilters,
  label,
  key,
  value,
}: {
  prevFilters: any;
  label: string;
  key: string;
  value: string;
}) => {
  const newFilters = { ...prevFilters };

  const processedValue = trimSpacesIfDigitsOrPrefixed(value);

  if (processedValue.length === 0) {
    delete newFilters[key];
  } else {
    newFilters[key] = {
      label,
      value: processedValue,
    };
  }

  return newFilters;
};

export const handleBooleanSummary = ({
  prevFilters,
  label,
  key,
  value,
}: {
  prevFilters: any;
  label: string;
  key: string;
  value: string;
}) => {
  const newFilters = { ...prevFilters };

  if (!value) {
    delete newFilters[key];
  } else {
    newFilters[key] = {
      label,
    };
  }

  return newFilters;
};

export const getFormattedDate = (date: Date) => {
  if (isYesterday(date)) {
    return `Yesterday, ${date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })}`;
  }

  if (isTomorrow(date)) {
    return `Tomorrow, ${date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })}`;
  }

  if (isToday(date)) {
    return `Today, ${date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })}`;
  }

  return `${date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })}, ${date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })}`;
};

export const dateToLongString = (date: Date): string => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

export const handleRedirect = (path: string) => {
  return `${EnvironmentVariables.CRM_NAVIGATE_URL}${path}`;
};

export const formatDateTimeWithDayFirst = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${day}/${month}/${year}, ${time}`;
};

export const transformTelNum = (telNum: any) => {
  if (telNum)
    switch (telNum.substring(0, 2)) {
      case "00":
        return telNum.substring(2);
      case "01":
        return `2${telNum}`;
      default:
        if (telNum?.startsWith("+")) return telNum.substring(1);
        return telNum;
    }
};

export const handleContactWhatsapp = (
  phoneNumber?: string | null,
  message?: string,
  username?: string | null,
) => {
  const link = buildWhatsappLink({ phone: phoneNumber, username }, message);
  if (link) window.open(link, "_blank", "noopener,noreferrer");
};

export const handleContactPhone = (phoneNumber?: string | null) => {
  const phone = normalizeContactPhone(phoneNumber);
  if (phone) window.location.href = `tel:${phone}`;
};

export const getReferralLabel = ({
  sale,
  leadHasActiveReferral,
}: {
  sale?: SalesResponseV2 | null;
  leadHasActiveReferral?: { referrer?: { name?: string } };
}): string => {
  const saleReferrerName = sale?.sale?.leadReferral?.referrer?.name;
  const leadReferrerName = leadHasActiveReferral?.referrer?.name;

  if (saleReferrerName) {
    return `Current Referral on this sale: ${saleReferrerName}`;
  }

  if (leadReferrerName && !sale) {
    return `Current Referral on this lead: ${leadReferrerName}`;
  }

  return "No Referral";
};

export const handleStringToBooleanValue = (val?: string) => {
  if (val === "true") return true;
  else if (val === "false") return false;
  else return undefined;
};

export const capitalizeFirstLetter = (
  text: string | undefined | null
): string => {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};
export const downloadFile = async (url: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = "";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export function downloadBlob(
  blob: Blob,
  options: {
    filename?: string;
    disposition?: string | null;
  } = {}
) {
  const { filename, disposition } = options;

  // 1️⃣ Extract filename from header if possible
  const parsedFilename =
    disposition?.match(/filename\*?=['"]?(?:UTF-8'')?([^\"';]+)["']?/)?.[1] ??
    filename ??
    "download";

  // 3️⃣ Create a temporary URL
  const url = URL.createObjectURL(blob);

  // 4️⃣ Create a temporary anchor link
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = decodeURIComponent(parsedFilename);
  anchor.style.display = "none";

  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  // 5️⃣ Clean up
  URL.revokeObjectURL(url);
}

export const mapRatingAgentRank = (userRank?: string | null): string => {
  if (userRank === "agent") {
    return `Assigned ${userRank}`;
  }
  return capitalizeFirstLetter(userRank);
};

export const formatDateTimeWithSeconds = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;

  return `${day}/${month}/${year}, ${displayHours}:${minutes}:${seconds} ${ampm}`;
};

export const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMinutes}m`;
};
